/**
 * Chat Conversation Hook
 * Higher-level hook for managing an active chat conversation
 */

import { useState, useCallback } from 'react';
import { useChatMutations } from './useChatMutations';
import { useChatStats } from './useChat';
import type { ChatMessage } from '../types/help.types';

interface UseChatOptions {
  currentPage?: string;
  sessionId?: string;
}

interface RateLimitInfo {
  remaining: number;
  limit: number;
  resetTime: string | null;
}

export function useChatConversation(options: UseChatOptions = {}) {
  const { currentPage = '/' } = options;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(options.sessionId || null);
  const [error, setError] = useState<string | null>(null);

  const { sendMessage } = useChatMutations();
  const { data: stats } = useChatStats();

  // Extract rate limit info from stats
  const rateLimit: RateLimitInfo | null = stats ? {
    remaining: stats.usage.messagesRemaining,
    limit: stats.usage.dailyLimit,
    resetTime: stats.usage.resetTime,
  } : null;

  const send = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // Add user message optimistically
    const userMessage: ChatMessage = {
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setError(null);

    try {
      const response = await sendMessage.mutateAsync({
        message: content.trim(),
        sessionId: sessionId || undefined,
        context: {
          currentPage,
        },
      });

      // Update session ID if this was the first message
      if (!sessionId && response.sessionId) {
        setSessionId(response.sessionId);
      }

      // Add assistant message
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.content,
        timestamp: new Date().toISOString(),
        suggestedActions: response.actions,
        articlesUsed: response.articlesUsed.map(a => a.slug),
        tokenUsage: {
          input: response.tokenUsage.inputTokens,
          output: response.tokenUsage.outputTokens,
        },
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      // Remove the optimistic user message on error
      setMessages(prev => prev.slice(0, -1));
    }
  }, [sessionId, currentPage, sendMessage]);

  const clearConversation = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  }, []);

  return {
    messages,
    sessionId,
    isLoading: sendMessage.isPending,
    error,
    rateLimit,
    send,
    clearConversation,
  };
}

export default useChatConversation;
